import { Tenant, type Asset, type WorkOrderTemplate } from '#/graphql/types';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NuvoGrid } from '@nuvolo/nuux-core/components/NuvoGrid';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Item from './Item';
import AssetSelect from './AssetSelect';
import TemplateViewer from './TemplateViewer';
import TemplatesList from './TemplatesList';
import { BLANK_TEXT } from '#/modules/wo-template/utils/constants';
import useWOTemplateForAsset from '#/modules/wo-template/hooks/useWOTemplateForAsset';
import TenantSelector from '#/common/components/TenantSelector';
import If from '#/common/components/If';
import useTenantQuery from '#/modules/admin/tenants/hooks/useTenantQuery';
import { NuvoTextField } from '@nuvolo/nuux-core/components/NuvoTextField';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import TMMButton from '#/common/components/TMMButton';
import { StyledNuvoBox } from '#/common/styles/template';
import { PageActions } from '#/common/components/SubPage';

export interface TemplatePickerProps {
  assetId?: string;
}

const TemplatePicker: React.FC<TemplatePickerProps> = ({ assetId: initialAssetId = '' }) => {
  const { isDesktop } = useViewport();
  const [asset, setAsset] = useState<Asset | undefined>(undefined);
  const [template, setTemplate] = useState<WorkOrderTemplate>();

  const templateQuery = useWOTemplateForAsset(asset);
  const templateResult = templateQuery.data?.getWorkOrderTemplateList?.data || undefined;

  const [searchParams] = useSearchParams();
  const tenantIdFromParams = searchParams.get('tenantId') || undefined;

  const [selectedTenant, setTenant] = useState<Tenant>();
  const preselectedTenant = useTenantQuery(tenantIdFromParams, { skip: !tenantIdFromParams }).data
    ?.getTenant?.data;

  const tenant = preselectedTenant || selectedTenant;

  const templates = useMemo(() => {
    const tmpl = templateResult?.filter((t) => !!t) || [];

    return [
      {
        id: BLANK_TEXT,
        __typename: 'WorkOrderTemplate',
        title: 'Blank template',
        description: 'Create Work Order from scratch.',
      },
      ...tmpl,
    ];
  }, [templateResult]) as WorkOrderTemplate[];

  const selectAsset = useCallback((newAsset: Asset | undefined) => {
    if (asset) {
      setTenant(asset.tenant ?? undefined);
    }

    setAsset(newAsset);
    setTemplate(undefined);
  }, []);

  const navigate = useNavigate();
  const next = useCallback(() => {
    if (!tenant?.id || !asset?.id || !template?.id) {
      return;
    }

    const params = new URLSearchParams();
    params.set('assetId', asset?.id);
    params.set('templateId', template.id);
    params.set('tenantId', tenant?.id);
    const query = params.toString();

    navigate(`../new?${query}`, { replace: true });
  }, [tenant?.id, asset?.id, navigate, template?.id]);

  return (
    <StyledNuvoBox isDesktop={isDesktop}>
      <div className="section-label">
        <NuvoLabel>Work Order Details</NuvoLabel>
      </div>

      {!tenantIdFromParams && <TenantSelector onTenantSelect={setTenant} />}

      <NuvoBox display="flex" flexDirection="column" gap={20} id="tmm-section-no-background">
        <div className="organization-wrapper" style={{ width: isDesktop ? '50%' : '100%' }}>
          <NuvoLabel>Organization</NuvoLabel>
          <NuvoTextField value={tenant?.name || '[ Loading... ]'} readOnly />
        </div>
      </NuvoBox>

      <NuvoBox display="flex" flexDirection="column" gap={20} id="tmm-section">
        <div className="organization-wrapper">
          <NuvoLabel>Related Asset Name</NuvoLabel>
          <AssetSelect
            asset={asset}
            onSelect={selectAsset}
            tenantId={tenant?.id}
            initialAssetId={initialAssetId}
          />
        </div>
      </NuvoBox>

      <div className="section-label">
        <NuvoLabel>Select Work Order Template</NuvoLabel>
      </div>
      <NuvoBox display="flex" flexDirection="column" gap={20} id="tmm-section">
        <NuvoGrid container>
          <NuvoGrid item xs={12} sm={6} className="tmm-section-panel">
            <Item>
              <If condition={!asset}>
                <NuvoText type="header4">Please, select asset!</NuvoText>
              </If>
              <If condition={!!asset}>
                <TemplatesList
                  loading={false}
                  asset={asset}
                  templates={templates}
                  onSelect={setTemplate}
                />
              </If>
            </Item>
          </NuvoGrid>

          <NuvoGrid item xs={12} sm={6} className="tmm-section-panel">
            <Item>
              <NuvoText type="header3">Template Preview</NuvoText>
              <TemplateViewer template={template} />
            </Item>
          </NuvoGrid>
        </NuvoGrid>
      </NuvoBox>

      <PageActions
        actions={[
          {
            id: 'new-wo-next',
            type: 'button',
            alwaysShow: true,
            props: {
              type: 'submit',
              label: 'Next',
              disabled: !tenant || !asset || !template,
              onClick: next,
            },
          },
        ]}
      />
    </StyledNuvoBox>
  );
};

export default React.memo(TemplatePicker);
